import { isRecord } from '@/utils/objects';
import type { AttributeNode } from '../types';
import { firstAndFilter, readFilterFields, toString } from './parse-helpers';

// Attribute-like types from backend: object_attribute, event_attribute, etc.
// These all share the same { field, operator, value } shape.
const ATTRIBUTE_LIKE_TYPES = new Set([
  'attribute',
  'object_attribute',
  'event_attribute',
  'foreign_event_attribute',
  'inbound_attribute',
  'collection_attribute',
]);

/**
 * Parse attribute conditions.
 * Handles: { event: { type: 'attribute_change', ... } }
 *          { attribute_filter: { field, operator, value } }  (backend type: 0)
 *          { type: 'attribute', field, operator, value }
 *          { field, operator, value }  (bare filter)
 */
export function parseAttribute(raw: Record<string, unknown>): AttributeNode | null {
  // Ember format: { event: { type: 'attribute_change', name, filters: { and: [to, from] } } }
  const event = isRecord(raw.event) ? raw.event : undefined;
  if (event?.type === 'attribute_change') {
    const name = toString(event.name);
    if (name == null) return null;
    return {
      type: 'attribute',
      field: name,
      ...readFilterFields(firstAndFilter(event)),
    };
  }

  // Backend format: { type: 0, attribute_filter: { field, operator, value } }
  if ('attribute_filter' in raw && isRecord(raw.attribute_filter)) {
    const af = raw.attribute_filter;
    const field = toString(af.attribute ?? af.field);
    if (field == null) return null;
    return { type: 'attribute', field, ...readFilterFields(af) };
  }

  // Backend format: { type: 4, related_object_attr_filter: {...} } or just { related_object_attr_filter: {...} }
  if (isRecord(raw.related_object_attr_filter)) {
    const af = raw.related_object_attr_filter;
    const field = toString(af.field ?? af.attribute);
    if (field == null) return null;
    return { type: 'attribute', field, ...readFilterFields(af) };
  }

  // Backend format: { type: 5, relationship_attr_filter: {...} } or just { relationship_attr_filter: {...} }
  if (isRecord(raw.relationship_attr_filter)) {
    const af = raw.relationship_attr_filter;
    const field = toString(af.field ?? af.attribute);
    if (field == null) return null;
    return { type: 'attribute', field, ...readFilterFields(af) };
  }

  // Direct format: { type: 'attribute', field, operator, value } or bare { field, operator, value }
  const condType = raw.type;
  const hasField = 'field' in raw || 'attribute' in raw;
  if (
    (typeof condType === 'string' && ATTRIBUTE_LIKE_TYPES.has(condType)) ||
    condType === 0 ||
    condType === '0' ||
    (hasField && condType == null)
  ) {
    const field = toString(raw.field ?? raw.attribute ?? raw.name);
    if (field == null) return null;
    return { type: 'attribute', field, ...readFilterFields(raw) };
  }

  // Condition reference: { condition_id: number, inverse }
  // These are saved filters stored by reference — show a placeholder since we can't fetch inline
  if (
    'condition_id' in raw &&
    typeof raw.condition_id === 'number' &&
    raw.condition_id > 0 &&
    !('field' in raw || 'attribute' in raw || 'event' in raw || 'segment' in raw)
  ) {
    const inverse = raw.inverse === true;
    return {
      type: 'attribute',
      field: `Saved Filter #${raw.condition_id}`,
      operator: inverse ? 'does not match' : 'matches',
      inverse,
    };
  }

  // Email validation: { type: 'email', operator: 'email_valid' | 'exists', inverse }
  if (condType === 'email') {
    const operator = toString(raw.operator);
    const inverse = raw.inverse === true;
    let operatorLabel: string;
    if (operator === 'email_valid') {
      operatorLabel = inverse ? 'is not a valid email' : 'is a valid email';
    } else if (operator === 'exists') {
      operatorLabel = inverse ? 'does not exist' : 'exists';
    } else {
      operatorLabel = inverse ? 'is not valid or does not exist' : 'is valid and exists';
    }
    return {
      type: 'attribute',
      field: 'email',
      operator: operatorLabel,
      inverse,
    };
  }

  // Newsletter recipients: { type: 'newsletter_recipients', newsletter_id, inverse }
  if (condType === 'newsletter_recipients' || 'newsletter_recipients' in raw) {
    const inverse = raw.inverse === true || raw.negate === true;
    const nr = isRecord(raw.newsletter_recipients) ? raw.newsletter_recipients : undefined;
    const newsletterId = toString(nr?.id ?? raw.newsletter_id);
    const newsletterName = toString(raw.newsletter_name ?? nr?.name);
    const name = newsletterName ?? (newsletterId != null ? `Newsletter #${newsletterId}` : 'newsletter');
    const prefix = inverse ? 'is not a recipient of' : 'is a recipient of';
    return {
      type: 'attribute',
      field: `${prefix} ${name}`,
      inverse,
    };
  }

  return null;
}
